'use client';

import React from 'react';
import RoughAnnotation from '@/components/common/RoughAnnotation';
import { useGsapReveal } from '@/hooks/useGsapReveal';

// Helper to highlight specific phrases in quotes
const highlightPhrase = (text: string, phrases: string[]) => {
  let result = text;
  phrases.forEach(phrase => {
    result = result.replace(
      phrase,
      `<mark class="highlight-marker">${phrase}</mark>`
    );
  });
  return result;
};

// Marker-style thought bubble SVG component
const ThoughtBubble: React.FC<{
  children: React.ReactNode;
  color?: string;
}> = ({ children, color = '#90b8f0' }) => (
  <div className="thought-bubble" style={{ '--bubble-color': color } as React.CSSProperties}>
    <svg className="thought-bubble__border" viewBox="0 0 400 180" preserveAspectRatio="none">
      <path
        d="M18,8 Q6,10 8,22 L6,138 Q10,152 22,148 L42,150 L32,172 L68,148 L378,152 Q394,148 390,138 L394,22 Q390,6 378,10 Z"
        stroke={color}
        strokeWidth="1.5"
        fill="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <div className="thought-bubble__content">
      {children}
    </div>
  </div>
);

// Wobbly vertical divider line
const WobblyVDivider: React.FC = () => (
  <svg className="social-proof__wobbly-divider" viewBox="0 0 4 50" fill="none">
    <path
      d="M2,0 Q3,12 1,25 Q-1,38 2,50"
      stroke="var(--color-border)"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

const SocialSection: React.FC = () => {
  const sectionRef = useGsapReveal<HTMLElement>({ y: 30, duration: 0.8 });

  const reviews = [
    {
      name: "Chandan Bhattacharya",
      role: "Maths Department HOD, Don Bosco School",
      quote: "We save ~15 hours of checking time every week, per centre.",
      highlights: ["~15 hours"],
    },
    {
      name: "Sunita Garg",
      role: "Lead Tutor, ACE Accountancy Classes",
      quote: "Made my life easy. Took hardly any time to start. Parents love the clarity in the report.",
      highlights: ["Made my life easy", "clarity in the report"],
    }
  ];

  return (
    <section id="social" ref={sectionRef} className="social-proof scroll-mt-32">
      <div className="social-proof__inner">
        <div className="social-proof__content">
          {/* LEFT: Thought Bubbles */}
          <div className={`social-proof__bubbles social-proof__reveal`}>
            {/* Decorative quotation mark — Caveat, rotated */}
            <span
              className="absolute -top-4 -left-2 select-none pointer-events-none"
              style={{
                fontFamily: 'var(--font-caveat)',
                fontSize: '140px',
                lineHeight: 1,
                opacity: 0.2,
                color: 'var(--color-text)',
                transform: 'rotate(-6deg)',
              }}
              aria-hidden="true"
            >"</span>
            {reviews.map((item, idx) => (
              <ThoughtBubble
                key={idx}
                color={idx === 0 ? '#90b8f0' : '#b8c4d4'}
              >
                <p
                  className="thought-bubble__quote"
                  dangerouslySetInnerHTML={{
                    __html: highlightPhrase(item.quote, item.highlights)
                  }}
                />
                <div className="thought-bubble__author">
                  <div className="thought-bubble__avatar" aria-hidden="true">
                    {item.name.split(' ').map(w => w[0]).join('')}
                  </div>
                  <div>
                    <div className="thought-bubble__name">{item.name}</div>
                    <div className="thought-bubble__role">{item.role}</div>
                  </div>
                </div>
              </ThoughtBubble>
            ))}
            {/* Closing quotation mark — mirrors the opening one */}
            <span
              className="absolute -bottom-4 -right-2 select-none pointer-events-none"
              style={{
                fontFamily: 'var(--font-caveat)',
                fontSize: '100px',
                lineHeight: 1,
                opacity: 0.12,
                color: 'var(--color-text)',
                transform: 'rotate(6deg)',
              }}
              aria-hidden="true"
            >&rdquo;</span>
          </div>

          {/* RIGHT: Stats */}
          <div className={`social-proof__stats-side social-proof__reveal`} style={{ transitionDelay: '150ms' }}>
            <div className="section-header section-header--secondary social-proof__header">
              <span className="section-eyebrow">What Customers Say</span>
              <h2 className="section-title text-center md:text-left">
                Results that tutors feel from Week 1.
              </h2>
              <p className="section-subtitle text-center md:text-left">
                Faster checking, clearer parent conversations, and better academic visibility in the first cycle.
              </p>
            </div>
            <div className="social-proof__stats">
              <div className="social-proof__stat">
                <div className="social-proof__stat-value">
                  <RoughAnnotation
                    type="circle"
                    color="#3b9a5b"
                    strokeWidth={1.5}
                    padding={6}
                    animationDuration={1000}
                    triggerOnScroll={true}
                    scrollThreshold={0.3}
                    delay={400}
                  >
                    7 hrs/week saved
                  </RoughAnnotation>
                </div>
                <div className="social-proof__stat-label">Checking time per teacher (avg.)</div>
              </div>
              <WobblyVDivider />
              <div className="social-proof__stat">
                <div className="social-proof__stat-value">Parent intelligence reports</div>
                <div className="social-proof__stat-label">Concept-mapped proof, not just marks</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialSection;
